import { useQuery } from "@tanstack/react-query";
import { eachDayOfInterval } from "date-fns";
import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import "highcharts/es-modules/masters/modules/stock.src.js";
import { useTranslation } from "react-i18next";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import Loading from "../../../components/core/blocks/Loading";
import { useAppSelector } from "../../../redux/hooks";
import { lightenColor } from "../../../utils/colors";
import { formatCurrency } from "../../../utils/formatNumber";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useGetV4CategoryReportByDate } from "../../perf_reports/api/category/v4CategoryReportByDate";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { useMemo } from "react";

export function TotalSalesChart({
  startDate,
  endDate,
  dailySalesGoal,
  daysInPeriod,
  categoryId,
}: {
  startDate: Date;
  endDate: Date;
  dailySalesGoal: number;
  daysInPeriod: number;
  categoryId?: number;
}) {
  const { t } = useTranslation(["goals", "metrics"]);
  const totalSalesGoalGraph = Array.from({ length: daysInPeriod }, () => dailySalesGoal);
  const totalSalesMetric = getMetricSpec("sales");
  const totalSalesMetricName = t(($) => $.metrics.byId.sales.name);

  const filters = useMemo(() => {
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...categoryFilter];
  }, [categoryId]);

  const byDateQuery = useQuery(
    useGetV4CategoryReportByDate()({
      start: startDate,
      end: endDate,
      group: "day",
      preStart: startDate,
      preEnd: startDate,
      filters: filters,
    }),
  );
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const axisDate = useDateFormatter("axis");

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  if (!byDateQuery.isSuccess) {
    return <DevErrorDisplay userMessage={t(($) => $.goals["Error loading stats"])} error={byDateQuery.error} />;
  }

  const options: Highcharts.Options = {
    title: { text: "" },
    chart: {
      type: "line",
      height: 350,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    tooltip: {
      formatter() {
        return `<span style="color:${this.series.color as string}">${this.series.name}</span>: <b>${formatCurrency(
          this.cumulativeSum,
          {
            marketPlace: marketPlace,
          },
        )}</b>`;
      },
    },
    xAxis: {
      categories: eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => axisDate.format(date)),
      labels: { step: 4 },
    },
    yAxis: {
      title: {
        text: undefined,
      },
    },
    series: [
      {
        name: t(($) => $.goals["{metricName} Expected"], { metricName: totalSalesMetricName }),
        data: totalSalesGoalGraph,
        type: "line",
        cumulative: true,
        color: lightenColor(totalSalesMetric.color, 1.9),
        dashStyle: "Dash",
        marker: { enabled: false },
      },
      {
        name: totalSalesMetricName,
        data: byDateQuery.data.data.slice(0, daysInPeriod).map(totalSalesMetric.accessor),
        type: "line",
        cumulative: true,
        color: totalSalesMetric.color,
      },
    ],
  };

  return <Chart options={options} />;
}
